// One-shot plate reader for gallery images (single photo).
//
// The live scanner (useScanner) streams video frames through a worker it owns;
// that worker is not reachable from the manual-lookup form or the details
// screen. So gallery parsing gets its own lazily-created worker — the same
// pipeline module, initialized on first use and then kept warm. Model bytes are
// already in the HTTP cache from the live scanner, so init is cheap after the
// first scan of the session.

import { MODELS } from '../../engine/pipeline/modelManifest';
import { matchPattern, normalize } from '../../engine/vote/normalize';
import type { OcrRead, WorkerToMain } from '../../engine/types';

export interface ImageScanResult {
  text: string;
  region: string | undefined;
}

let worker: Worker | null = null;
let readyPromise: Promise<void> | null = null;
let frameId = 0;
const pending = new Map<number, (read: OcrRead | null) => void>();

function ensureWorker(): Promise<void> {
  if (readyPromise) return readyPromise;
  readyPromise = new Promise((resolve, reject) => {
    const w = new Worker(new URL('../../engine/pipeline/worker.ts', import.meta.url), {
      type: 'module',
    });
    w.onmessage = (ev: MessageEvent<WorkerToMain>) => {
      const msg = ev.data;
      if (msg.t === 'ready') resolve();
      else if (msg.t === 'result') {
        const settle = pending.get(msg.frameId);
        if (settle) {
          pending.delete(msg.frameId);
          settle(msg.read ?? null);
        }
      } else if (msg.t === 'error' && msg.stage === 'init') {
        reject(new Error(msg.message));
      }
    };
    w.onerror = (e) => reject(new Error(e.message));
    w.postMessage({ t: 'init', detectorUrl: MODELS.detector.url, ocrUrl: MODELS.ocr.url });
    worker = w;
  });
  readyPromise = readyPromise.catch((err) => {
    // A failed init must not wedge every future attempt behind a rejected promise.
    readyPromise = null;
    worker = null;
    throw err;
  });
  return readyPromise;
}

/**
 * Detect + OCR a single image file. Returns the cleaned plate text (pattern-
 * corrected like the live voting path) and detected region, or null when no
 * plausible plate is found.
 */
export async function scanImageFile(file: File): Promise<ImageScanResult | null> {
  await ensureWorker();
  const w = worker;
  if (!w) return null;
  const bitmap = await createImageBitmap(file);
  const read = await new Promise<OcrRead | null>((resolve) => {
    const id = ++frameId;
    pending.set(id, resolve);
    // A still can never lock via temporal voting, so run a single auto-mode pass
    // with perspective rectification on — that is what straightens angled plates.
    w.postMessage({ t: 'frame', bitmap, mode: 'auto', rectify: true, frameId: id }, [bitmap]);
    setTimeout(() => {
      if (pending.delete(id)) resolve(null);
    }, 20_000);
  });
  if (!read || read.raw.length === 0) return null;
  const match = matchPattern(normalize(read.raw));
  const text = match ? match.text : normalize(read.raw);
  if (text.length < 2) return null;
  return { text, region: read.region?.name };
}
